import { Badge, Button, Group, Loader, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconMicrophone, IconPencil, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { FeedItemComments } from 'src/Components/Comments/FeedItemComments';
import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { LinkTrail } from 'src/Components/EntityLinks/LinkTrail';
import { MEMORY_KIND_COLOR, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { VoiceStoryPlayer } from 'src/Components/Memories/VoiceStoryPlayer';
import { VoiceStoryRecorder } from 'src/Components/Memories/VoiceStoryRecorder';
import { useDeleteMemory, useDeleteMemoryRecording } from 'src/Hooks/Mutations/useMemoryMutations';
import useFetchMemory from 'src/Hooks/Queries/useFetchMemory';
import useFetchSingleFeedItem from 'src/Hooks/Queries/useFetchSingleFeedItem';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { MemoryKind } from 'src/Services/API/Memories.api';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './MemoryDetailPage.module.scss';

const MemoryDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { memoryId } = useParams<{ memoryId: string }>();
  const { data: memory, isLoading, isError } = useFetchMemory(memoryId);
  // The written memories are the album's comments: same component as Ma communauté.
  const { data: feedItem } = useFetchSingleFeedItem(memoryId ?? '');
  const location = useLocation();
  // Right after "Ajouter à la mémoire de la maison", go straight to telling it out loud.
  const [recorderOpened, recorder] = useDisclosure(
    !!(location.state as { openRecorder?: boolean } | null)?.openRecorder
  );
  const { mutate: deleteMemory } = useDeleteMemory(memoryId ?? '');
  const { mutate: deleteRecording } = useDeleteMemoryRecording(memoryId ?? '');

  if (isLoading) return <Loader />;
  if (isError || !memory) {
    return <Text>{t('memories.detail.error', 'Impossible de retrouver ce souvenir.')}</Text>;
  }

  const timelinePath = `/asset/${assetId}/memories`;
  const dateLabel = formatMemoryDate(
    t,
    memory.takenAt,
    memory.takenAtPrecision,
    memory.takenAtApproximate
  );

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t('memories.detail.deleteTitle', 'Supprimer ce souvenir ?'),
      children: (
        <Text size="sm">
          {t(
            'memories.detail.deleteMessage',
            'Il disparaîtra de la mémoire de la maison et de Ma communauté. Les photos restent dans Documents.'
          )}
        </Text>
      ),
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteMemory(undefined, {
          onSuccess: () => {
            notifications.show({
              color: 'teal',
              message: t('memories.detail.deleted', 'Souvenir supprimé.'),
            });
            navigate(timelinePath);
          },
          onError: () => {
            notifications.show({
              color: 'red',
              message: t(
                'memories.detail.deleteError',
                "Le souvenir n'a pas pu être supprimé. Réessayez dans un instant."
              ),
            });
          },
        }),
    });

  return (
    <div className={styles.container}>
      <Link to={timelinePath}>
        <Button variant="transparent" leftSection={<IconArrowLeft size={18} />}>
          {t('memories.detail.back', 'Toute la mémoire de la maison')}
        </Button>
      </Link>
      <LinkTrail />

      <header className={styles.header}>
        <Group gap="xs">
          {dateLabel && <span className={styles.date}>{dateLabel}</span>}
          {memory.memoryKind && memory.memoryKind !== MemoryKind.InMemory && (
            <Badge variant="light" color={MEMORY_KIND_COLOR[memory.memoryKind]}>
              {memoryKindLabel(t, memory.memoryKind)}
            </Badge>
          )}
          {memory.memoryKind === MemoryKind.InMemory && (
            <span className={styles.date}>{memoryKindLabel(t, memory.memoryKind)}</span>
          )}
        </Group>
        <h1 className={styles.title}>{memory.title}</h1>
        <Text className={styles.subtitle}>
          {[
            memory.place,
            memory.rooms.map(room => room.name).join(', '),
            t('memories.detail.by', 'raconté par {{name}}', { name: memory.author.displayName }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {memory.canEdit && (
          <Group gap="xs">
            <Button
              variant="light"
              leftSection={<IconPencil size={18} />}
              onClick={() => navigate(`${timelinePath}/${memory.id}/edit`)}
            >
              {t('memories.detail.edit', 'Modifier / ajouter des photos')}
            </Button>
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={18} />}
              onClick={confirmDelete}
            >
              {t('common.delete', 'Supprimer')}
            </Button>
          </Group>
        )}
      </header>

      {memory.files.length > 0 && (
        <div className={styles.gallery}>
          {memory.files.map(file => (
            <a key={file.id} href={file.url} target="_blank" rel="noreferrer">
              <img src={file.previewLargeUrl ?? file.url} alt="" loading="lazy" />
            </a>
          ))}
        </div>
      )}

      {memory.description && <Text className={styles.description}>{memory.description}</Text>}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('memories.detail.stories', 'Les récits')}</h2>
          <Button size="md" leftSection={<IconMicrophone size={20} />} onClick={recorder.open}>
            {t('memories.detail.addStory', 'J’ajoute mon souvenir')}
          </Button>
        </div>
        {memory.recordings.length === 0 ? (
          <Text c="dimmed">
            {t(
              'memories.detail.noStory',
              'Personne n’a encore raconté ce souvenir à voix haute. Et si c’était vous ?'
            )}
          </Text>
        ) : (
          memory.recordings.map(recording => (
            <VoiceStoryPlayer
              key={recording.id}
              recording={recording}
              onDelete={recordingId =>
                deleteRecording(recordingId, {
                  onError: () => {
                    notifications.show({
                      color: 'red',
                      message: t(
                        'memories.recording.deleteError',
                        "Le récit n'a pas pu être retiré. Réessayez dans un instant."
                      ),
                    });
                  },
                })
              }
            />
          ))
        )}
      </section>

      {memory.people.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('memories.detail.people', 'Qui était là')}</h2>
          <Group gap="xs">
            {memory.people.map(person => (
              <Badge key={person.id} size="lg" variant="light" color="gray">
                {person.displayName}
              </Badge>
            ))}
          </Group>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('memories.detail.linked', 'Liés à ce souvenir')}</h2>
        <LinkedEntitiesSection
          entityType={LinkableEntityType.CommunityFeedItem}
          entityId={memory.id}
          entityTitle={memory.title}
        />
      </section>

      {feedItem && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('memories.detail.comments', 'Ils s’en souviennent aussi')}
          </h2>
          <FeedItemComments feedItem={feedItem} />
        </section>
      )}

      <VoiceStoryRecorder memoryId={memory.id} opened={recorderOpened} onClose={recorder.close} />
    </div>
  );
};

export default MemoryDetailPage;
